import BlastTrace from "../../components/BlastTrace";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Printer, AlertTriangle, RefreshCw, FileText, ClipboardCheck, Store, Truck, Info, CheckCircle2, XCircle, Hourglass, HelpCircle, Calculator, Download, FileDown, Zap, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, openServerPdf } from "../../lib/api";
import { Card, Button, Select, Field, Spinner, Input, inputCls } from "../../components/ui-kit";
import { formatItalianDate } from "../../lib/dateUtils";
import { LotTraceLink } from "../../components/LotTrace";
import { ShelfLifeResultBadge, ShelfLifeWhyDrawer } from "../../components/ShelfLifeWhyDrawer";
import { NutritionResultBadge, NutritionWhyDrawer } from "../../components/NutritionWhyDrawer";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";
import { setPrintPageSize, clearPrintPageSize } from "../../lib/printPageSize";
import { isIOS } from "../../lib/platform";
import { openServerPdfFile, pdfFileName, preparePdfWindow } from "../../lib/pdfExport";

import { LABEL_FORMATS as FORMATS } from "../../lib/labelFormats";
import { conservationDisplayText } from "../../lib/conservationText";
import { frozenDenomination, thawInstructions } from "../../lib/frozenLabel";
import { ConsumerLabelChecklist, ConsumerLabelPreview, LabelProfileEditor, LabelPrintLog } from "../../components/ConsumerLabel";
import MocaPrompt from "../../components/MocaPrompt";
import MocaProductionDialog from "../../components/MocaProductionDialog";
import { mocaLabelText } from "../../lib/moca";
import NetQuantitySection, { PACK_KEYS } from "../../components/NetQuantitySection";
import { computePack, netQuantityLine, upgradeLegacyPack, withPack } from "../../lib/netQuantity";

// Le schede "B2B" e "Preimballato" usano entrambe labelType "commercial" e si
// distinguono per labelData.destination (stesso formato atteso dal backend).
const LABEL_TYPES = [
  { key: "internal", label: "Interna HACCP", icon: ClipboardCheck, description: "Per identificazione, autocontrollo, rintracciabilità interna ed esposizione in vetrina." },
  { key: "direct", label: "Vendita diretta", icon: Store, description: "Per prodotti venduti o somministrati direttamente al consumatore." },
  { key: "b2b", label: "B2B / fornitura", icon: Zap, description: "Rapida: per prodotti e semilavorati forniti ad altre attività. Scegli la quantità e stampa." },
  { key: "consumer", label: "Preimballato consumatore", icon: Truck, description: "Completa: preimballato per il consumatore finale, con dichiarazione nutrizionale." },
];
const LABEL_HEADER = { internal: "Interna HACCP", direct: "Vendita diretta", commercial: "Commerciale / B2B" };
const labelCardKey = (labelType, labelData) => labelType === "commercial" ? (labelData.destination === "consumer" ? "consumer" : "b2b") : labelType;

// Dati che si ripetono a ogni produzione della stessa ricetta: ricordati per
// ricetta, così la prossima etichetta B2B parte già compilata.
const RECIPE_MEMORY_KEYS = ["destination", "netQuantity", "packComposition", ...PACK_KEYS, "origin", "quid", "expiryType", "glutenFree"];
const PACK_EDITORS = ["super_admin", "titolare", "responsabile"];

// Etichetta B2B: sempre verticale, larga quanto il nastro (es. 62 mm del
// 62×100), lunga almeno quanto il formato e più lunga solo se il testo lo
// richiede (come _render_b2b in label_pdf.py). Oltre 40 cm non si stampa.
const MAX_LONG_LABEL_MM = 400;

// Misura l'etichetta applicando per un istante le dimensioni fisiche di stampa.
function measureLabel(el, styles, read) {
  const prevStyle = el.getAttribute("style") || "";
  Object.entries(styles).forEach(([key, value]) => (key.startsWith("--") ? el.style.setProperty(key, value) : (el.style[key] = value)));
  const result = read(el);
  el.setAttribute("style", prevStyle);
  return result;
}
const overflowsFormat = (el, format) => measureLabel(el, { width: `${format.widthMm}mm`, height: `${format.heightMm}mm`, minHeight: "0", overflow: "hidden" },
  (node) => node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1);
// Etichetta B2B sempre 62×100: il driver Brother stampa il rotolo continuo a
// lunghezza fissa, quindi un'etichetta più lunga verrebbe rimpicciolita e
// stretta. Si cerca la riduzione uniforme (--k) più grande con cui il testo
// entra nel formato, come production_label_layout in label_pdf.py; sotto
// B2B_MIN_SCALE l'etichetta si allunga invece di rimpicciolire ancora.
const B2B_MIN_SCALE = 0.65;
// La misura si fa alla dimensione reale di stampa (--u = 1 mm): l'anteprima a
// schermo è ingrandita e il testo non va a capo in modo esattamente
// proporzionale, quindi misurata lì l'ultima riga poteva uscire tagliata.
// B2B_PRINT_SAFETY_MM lascia un piccolo margine per le differenze di resa.
const B2B_PRINT_SAFETY_MM = 2;
const b2bFit = (el, format) => {
  const limit = format.widthMm - B2B_PRINT_SAFETY_MM;
  const neededMm = (k) => measureLabel(el, { "--u": "1mm", height: "auto", minHeight: "0", "--k": String(k) },
    (node) => (node.scrollHeight * 25.4) / 96 + 1);
  let scale = 1;
  if (neededMm(1) > limit) {
    let lo = B2B_MIN_SCALE, hi = 1;
    if (neededMm(lo) > limit) hi = lo;
    else for (let i = 0; i < 8; i += 1) { const mid = (lo + hi) / 2; if (neededMm(mid) <= limit) lo = mid; else hi = mid; }
    scale = Math.round(lo * 1000) / 1000;
  }
  const needed = neededMm(scale);
  return { scale, lengthMm: needed <= limit ? format.widthMm : Math.ceil(needed + B2B_PRINT_SAFETY_MM) };
};

const readStorage = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
};

const EMPTY_LABEL_DATA = {
  destination: "consumer", ingredientsText: "", netQuantity: "", expiryType: "use_by", origin: "", quid: "",
  nutritionExempt: false, exemptionReason: "", defrosted: false, traceConfirmed: false, glutenFree: false,
  operatorName: "", operatorAddress: "", instructions: "", conservationOverride: "",
  energyKj: "", energyKcal: "", fat: "", saturates: "", carbohydrate: "", sugars: "", protein: "", salt: "",
};

const SUGGESTIONS = {
  consumption: ["Pronto al consumo", "Consumare previa cottura", "Consumare dopo accurato riscaldamento", "Una volta aperto conservare in frigorifero e consumare entro 24 ore", "Una volta aperto conservare in frigorifero e consumare entro 48 ore", "Non ricongelare dopo lo scongelamento"],
  conservation: ["Conservare in frigorifero tra 0 °C e +4 °C", "Conservare in frigorifero a temperatura non superiore a +4 °C", "Conservare a temperatura non superiore a -18 °C", "Conservare in luogo fresco e asciutto, lontano da fonti di calore", "Dopo l’apertura conservare in frigorifero", "Mantenere la catena del freddo"],
  quantity: ["100 g", "150 g", "200 g", "250 g", "500 g", "750 g", "1 kg"],
  origin: ["Origine Italia", "Origine UE", "Origine non UE", "Origine UE e non UE"],
  quid: ["Ingrediente caratterizzante 10%", "Ingrediente caratterizzante 25%", "Ingrediente caratterizzante 50%", "Ingrediente caratterizzante 70%"],
  exemption: ["Fornitura diretta di piccole quantità nei casi previsti dalla normativa", "Prodotto escluso dalla dichiarazione nutrizionale ai sensi dell’Allegato V del Reg. (UE) 1169/2011"],
};

const NUTRIENTS = [
  ["energyKj", "Energia (kJ)"], ["energyKcal", "Energia (kcal)"], ["fat", "Grassi (g)"],
  ["saturates", "di cui saturi (g)"], ["carbohydrate", "Carboidrati (g)"], ["sugars", "di cui zuccheri (g)"],
  ["protein", "Proteine (g)"], ["salt", "Sale (g)"],
];

const automaticIngredients = (prod) => (prod?.recipe_ingredients || [])
  .slice()
  .sort((a, b) => (Number(b.qty) || 0) - (Number(a.qty) || 0))
  .map((item) => {
    const allerg = (item.allergens || []).map((key) => {
      const index = (prod.allergens || []).indexOf(key);
      return (prod.allergen_labels || [])[index] || key;
    });
    return `${item.name || item.ingredient_id}${allerg.length ? ` (${allerg.join(", ").toUpperCase()})` : ""}`;
  }).join(", ");

// Diciture degli allergeni (quelle generate e quelle brevi scritte a mano).
const ALLERGEN_WORDS = {
  GLUTINE: "glutine", CROSTACEI: "crostacei", UOVA: "uova", PESCE: "pesce", ARACHIDI: "arachidi", SOIA: "soia",
  LATTE: "latte", "LATTE (INCLUSO LATTOSIO)": "latte", "FRUTTA A GUSCIO": "frutta_guscio", SEDANO: "sedano",
  SENAPE: "senape", SESAMO: "sesamo", "SEMI DI SESAMO": "sesamo", SOLFITI: "solfiti",
  "ANIDRIDE SOLFOROSA E SOLFITI": "solfiti", LUPINI: "lupini", MOLLUSCHI: "molluschi",
};

// Elenco ingredienti ricordato nel browser con tra parentesi un allergene
// che la produzione non ha più (es. corretto in Materie prime): va rigenerato.
const staleAllergenText = (text, prod) => {
  const items = prod?.recipe_ingredients || [];
  const current = new Set(items.length
    ? items.flatMap((i) => [...(i.allergens || []), ...(i.traces || [])])
    : [...(prod?.allergens || []), ...(prod?.traces || [])]);
  const groups = String(text || "").toUpperCase().match(/\(((?:[^()]|\([^()]*\))*)\)/g) || [];
  return groups.some((group) => group.slice(1, -1).split(",").some((token) => {
    const key = ALLERGEN_WORDS[token.trim()];
    return key && !current.has(key);
  }));
};

function missingLabelFields(labelType, labelData, format, prod) {
  if (labelType === "internal") return [];
  const missing = [];
  if (format.small) missing.push("usa un formato minimo 50×100 mm");
  if (!labelData.ingredientsText.trim()) missing.push("elenco ingredienti");
  if (!(labelData.conservationOverride ?? prod?.conservation ?? "").trim()) missing.push("modalità di conservazione");
  if (labelData.defrosted === null) missing.push("stato decongelato");
  if (labelType === "commercial") {
    if (!labelData.operatorName.trim()) missing.push("ragione sociale dell’operatore");
    if (!labelData.operatorAddress.trim()) missing.push("indirizzo dell’operatore");
    const packError = labelData.packMode && labelData.packMode !== "single" ? computePack(labelData).error : null;
    if (packError) missing.push(packError);
    else if (!labelData.netQuantity.trim()) missing.push("quantità netta");
    if (labelData.destination === "consumer" && !labelData.nutritionExempt) {
      NUTRIENTS.forEach(([key, label]) => { if (!String(labelData[key]).trim()) missing.push(label); });
    }
    if (labelData.destination === "consumer" && labelData.nutritionExempt && !labelData.exemptionReason.trim()) missing.push("motivazione dell’esenzione nutrizionale");
  }
  if ((prod?.trace_labels || []).length && !labelData.traceConfirmed) missing.push("conferma della valutazione delle tracce");
  return missing;
}

export default function Etichette() {
  const { ctx, isLocked } = useOutletContext();
  const mocaOn = !isLocked("moca");
  const [sp] = useSearchParams();
  const [prods, setProds] = useState(null);
  const [prodId, setProdId] = useState(sp.get("prod") || "");
  const [prod, setProd] = useState(null);
  const [fmt, setFmt] = useState("62x100");
  const [labelType, setLabelType] = useState("internal");
  const [labelData, setLabelData] = useState(EMPTY_LABEL_DATA);
  const [loadedLabelProdId, setLoadedLabelProdId] = useState("");
  const [sleAssessment, setSleAssessment] = useState(null);
  const [showWhy, setShowWhy] = useState(false);
  const labelRef = useRef(null);
  const [nutritionAssessment, setNutritionAssessment] = useState(null);
  const [showNutritionWhy, setShowNutritionWhy] = useState(false);
  const [computingNutrition, setComputingNutrition] = useState(false);
  // Etichetta per il consumatore finale (preimballato): validazione normativa
  // eseguita dal server (routers_labels.py) e conferme degli avvisi arancioni.
  const [consumerValidation, setConsumerValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationKey, setValidationKey] = useState(0);
  const [confirmedWarnings, setConfirmedWarnings] = useState([]);
  const [labelMeta, setLabelMeta] = useState(null);
  const [printLogKey, setPrintLogKey] = useState(0);
  const consumerMode = labelType === "commercial" && labelData.destination === "consumer";
  const b2bMode = labelType === "commercial" && !consumerMode;
  const [b2bDetails, setB2bDetails] = useState(false);
  const [b2bSize, setB2bSize] = useState(null);
  const canEditProfile = ["titolare", "super_admin"].includes(ctx?.user?.role);
  // Configurazione abituale della confezione (salvata sul server per prodotto).
  const [packDefaults, setPackDefaults] = useState(null);
  const [savingPackDefaults, setSavingPackDefaults] = useState(false);
  const changePack = (patch, fallbackNet) => setLabelData((current) => {
    const next = withPack(current, patch);
    return !next.netQuantity && fallbackNet ? { ...next, netQuantity: fallbackNet } : next;
  });
  const savePackDefaults = async () => {
    if (!prod?.recipe_id) return;
    setSavingPackDefaults(true);
    try {
      const body = { ...Object.fromEntries(PACK_KEYS.map((k) => [k, String(labelData[k] ?? "")])), netQuantity: labelData.netQuantity };
      body.packMode = body.packMode || "single";
      body.packPieceUnit = body.packPieceUnit || "g";
      const { data } = await api.put(`/app/labels/pack-defaults/by-recipe/${prod.recipe_id}`, body);
      setPackDefaults(data.defaults);
      toast.success("Configurazione abituale salvata: verrà proposta alle prossime etichette di questo prodotto.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingPackDefaults(false); }
  };

  useEffect(() => {
    api.get("/app/productions").then(({ data }) => {
      setProds(data.productions);
    });
  }, []);
  useEffect(() => {
    if (!prodId) return undefined;
    // Cambio rapido di produzione: le risposte arrivate tardi per la
    // produzione precedente vengono ignorate.
    let cancelled = false;
    setSleAssessment(null);
    api.get("/app/shelf-life/assessments", { params: { production_id: prodId } })
      .then(({ data }) => !cancelled && setSleAssessment(data.assessments?.[0] || null))
      .catch(() => !cancelled && setSleAssessment(null));
    setNutritionAssessment(null);
    setPackDefaults(null);
    api.get(`/app/productions/${prodId}`).then(async ({ data }) => {
      // Configurazione abituale della confezione: proposta solo se questa
      // produzione non ha già dati di etichetta propri.
      const defaults = data.production.recipe_id
        ? await api.get(`/app/labels/pack-defaults/by-recipe/${data.production.recipe_id}`).then(({ data: d }) => d.defaults).catch(() => null)
        : null;
      if (cancelled) return;
      setPackDefaults(defaults);
      setProd(data.production);
      if (data.production.recipe_id) {
        api.get("/app/nutrition/assessments", { params: { recipe_id: data.production.recipe_id } })
          .then(({ data: nutri }) => !cancelled && setNutritionAssessment(nutri.assessments?.[0] || null))
          .catch(() => !cancelled && setNutritionAssessment(null));
      }
      const recipeSaved = data.production.recipe_id ? readStorage(`df-label-recipe-${data.production.recipe_id}`) : null;
      const prodSaved = readStorage(`df-label-${prodId}`);
      const saved = prodSaved || (defaults
        ? { ...recipeSaved, labelData: { ...(recipeSaved?.labelData || {}), ...Object.fromEntries(PACK_KEYS.map((k) => [k, defaults[k] ?? ""])), netQuantity: defaults.netQuantity || "", packComposition: computePack(defaults).composition } }
        : recipeSaved);
      setB2bDetails(false);
      const contacts = ctx?.tenant?.contacts || {};
      const savedIngredients = saved?.labelData?.ingredientsText || "";
      const staleIngredients = savedIngredients && staleAllergenText(savedIngredients, data.production);
      if (staleIngredients) toast.warning("Elenco ingredienti rigenerato: gli allergeni della ricetta sono cambiati rispetto al testo salvato. Ricontrollalo prima di stampare.");
      setLabelType(saved?.labelType || "internal");
      setLabelData(upgradeLegacyPack({
        ...EMPTY_LABEL_DATA,
        ...(saved?.labelData || {}),
        ingredientsText: (!staleIngredients && savedIngredients) || automaticIngredients(data.production),
        operatorName: (saved?.labelData?.operatorName || ctx?.tenant?.name || "").toUpperCase(),
        operatorAddress: (saved?.labelData?.operatorAddress || contacts.address || "").toUpperCase(),
        // Prodotto congelato ceduto ad altre attività: alle istruzioni d'uso
        // della ricetta si aggiungono quelle di scongelamento.
        instructions: (saved?.labelData?.instructions ?? [data.production.consumption, thawInstructions(data.production.thaw_hours, data.production.post_thaw_days)].filter(Boolean).join(" ")).toUpperCase(),
        conservationOverride: conservationDisplayText(saved?.labelData?.conservationOverride ?? data.production.conservation ?? "").toUpperCase(),
        // Simboli delle unità di misura in minuscolo (g, kg): niente maiuscolo forzato.
        netQuantity: saved?.labelData?.netQuantity || "",
        origin: (saved?.labelData?.origin || "").toUpperCase(),
        quid: (saved?.labelData?.quid || "").toUpperCase(),
        exemptionReason: (saved?.labelData?.exemptionReason || "").toUpperCase(),
      }));
      setLoadedLabelProdId(prodId);
    });
    return () => { cancelled = true; };
  }, [prodId, ctx]);

  useEffect(() => {
    if (consumerMode && !labelMeta) api.get("/app/labels/meta").then(({ data }) => setLabelMeta(data)).catch(() => {});
  }, [consumerMode, labelMeta]);

  useEffect(() => {
    if (!consumerMode || !prod || prod.id !== prodId) { setConsumerValidation(null); return undefined; }
    let active = true;
    setValidating(true);
    const timer = setTimeout(() => {
      api.post(`/app/labels/productions/${prodId}/validate`, { format: fmt, label_data: labelData })
        .then(({ data }) => {
          if (!active) return;
          setConsumerValidation(data.validation);
          const codes = data.validation.warnings.map((w) => w.code);
          setConfirmedWarnings((current) => current.filter((code) => codes.includes(code)));
        })
        .catch(() => active && setConsumerValidation(null))
        .finally(() => active && setValidating(false));
    }, 400);
    return () => { active = false; clearTimeout(timer); };
  }, [consumerMode, prod, prodId, fmt, labelData, validationKey]);

  useEffect(() => {
    if (!prodId || loadedLabelProdId !== prodId) return;
    try {
      localStorage.setItem(`df-label-${prodId}`, JSON.stringify({ labelType, labelData }));
      if (prod?.id === prodId && prod.recipe_id && labelType !== "internal") {
        const memory = Object.fromEntries(RECIPE_MEMORY_KEYS.map((key) => [key, labelData[key]]));
        localStorage.setItem(`df-label-recipe-${prod.recipe_id}`, JSON.stringify({ labelType, labelData: memory }));
      }
    } catch { /* memoria locale non disponibile: si compila a mano */ }
  }, [prodId, prod, loadedLabelProdId, labelType, labelData]);

  const computeNutrition = async () => {
    if (!prod?.recipe_id) { toast.error("Produzione non collegata a una ricetta: impossibile calcolare la dichiarazione."); return; }
    setComputingNutrition(true);
    try {
      const { data } = await api.post(`/app/nutrition/recipes/${prod.recipe_id}/compute`);
      setNutritionAssessment(data.assessment);
      const values = data.assessment.result.per_100g_cooked || data.assessment.result.per_100g_raw;
      if (values) {
        setLabelData((current) => ({
          ...current, energyKj: String(values.energy_kj ?? ""), energyKcal: String(values.kcal ?? ""),
          fat: String(values.fat_g ?? ""), saturates: String(values.saturates_g ?? ""),
          carbohydrate: String(values.carbs_g ?? ""), sugars: String(values.sugars_g ?? ""),
          protein: String(values.protein_g ?? ""), salt: String(values.salt_g ?? ""),
        }));
        toast.success(data.assessment.result.needs_validation
          ? "Valori calcolati: richiedono validazione tecnica prima della stampa (vedi \"Perché?\")."
          : "Valori nutrizionali calcolati dalla ricetta.");
      } else {
        toast.error("Impossibile calcolare: nessun ingrediente della ricetta ha una quantità in grammi/kg valorizzata o collegata al catalogo nutrizionale.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setComputingNutrition(false); }
  };

  const chooseLabelType = (key) => {
    if (key === "b2b" || key === "consumer") {
      setLabelType("commercial");
      setLabelData((current) => ({ ...current, destination: key === "consumer" ? "consumer" : "professional" }));
    } else setLabelType(key);
    if (key !== "internal" && FORMATS.find((f) => f.key === fmt)?.small) setFmt("50x100");
  };

  // Due flussi di stampa alternativi (vedi lib/platform.js):
  // - iOS Safari: PDF vero generato dal backend e aperto in una scheda, perché Safari
  //   aggiunge sempre un header (URL) e un footer (data/ora, numero pagina) quando si
  //   stampa una PAGINA WEB, senza modo di disattivarlo via CSS — su un'etichetta che usa
  //   già tutta l'area fisica disponibile questo spazio extra sovrappone testo estraneo e
  //   spinge il contenuto su un secondo foglio fantasma.
  // - Android/desktop: window.print() diretto (nessun header/footer aggiunto da Chrome in
  //   stampa, e il gestore PDF di terze parti su Android è troppo incoerente da dispositivo
  //   a dispositivo — a volte lo apre un visualizzatore che non rispetta il formato pagina —
  //   per essere una base affidabile).
  // asPdf: pulsante "Crea PDF e stampa", stesso PDF del flusso iOS su qualunque
  // dispositivo (dal telefono è più affidabile della stampa diretta della pagina).
  const print = async (target = "public", asPdf = false) => {
    if (!prod) return;
    if (target === "public" && consumerMode) {
      await printConsumer(asPdf);
      return;
    }
    if (target === "public" && missingFields.length) {
      toast.error(`Etichetta non pronta: completa ${missingFields.join(", ")}.`);
      return;
    }
    let b2bPrintLength = null;
    if (target === "public" && b2bLayout) {
      // Etichetta B2B: non si taglia mai, si allunga mantenendo la larghezza del nastro.
      const fit = labelRef.current ? b2bFit(labelRef.current, format) : { scale: 1, lengthMm: format.widthMm };
      b2bPrintLength = fit.lengthMm;
      if (b2bPrintLength > MAX_LONG_LABEL_MM) {
        toast.error(`Il contenuto richiede un'etichetta più lunga di ${MAX_LONG_LABEL_MM / 10} cm: riduci il testo (ad esempio ingredienti o istruzioni).`);
        return;
      }
      flushSync(() => setB2bSize(fit));
    } else if (target === "public") {
      const el = labelRef.current;
      // Misura se il contenuto entra nel formato fisico reale, prima di
      // stampare: senza questo controllo il testo in eccesso verrebbe
      // tagliato silenziosamente sull'etichetta stampata.
      if (el && overflowsFormat(el, format)) {
        toast.error("Il contenuto supera il formato etichetta selezionato: alcune informazioni verrebbero tagliate. Scegli un formato più grande o riduci il testo.");
        return;
      }
    }
    if (asPdf) {
      const win = preparePdfWindow();
      try {
        if (target === "internal") {
          await openServerPdfFile(`/app/productions/${prodId}/internal-label.pdf`, { fileName: pdfFileName("qr-fisso", prod.recipe_name), win });
        } else {
          api.post(`/app/productions/${prodId}/print`).catch(() => {});
          await openServerPdfFile(`/app/productions/${prodId}/label.pdf`, { method: "post", body: { format: fmt, label_type: labelType, label_data: labelData, qr_url: qrUrl }, fileName: pdfFileName("etichetta", prod.recipe_name, prod.lot_code), win });
        }
      } catch {
        toast.error("Impossibile generare il PDF dell'etichetta.");
      }
      return;
    }
    if (!isIOS()) {
      if (target === "public") api.post(`/app/productions/${prodId}/print`).catch(() => {});
      if (target === "public" && b2bPrintLength) setPrintPageSize(format.heightMm, b2bPrintLength);
      else if (target === "public") setPrintPageSize(format.widthMm, format.heightMm);
      else setPrintPageSize(62, 29); // QR interno: sempre lo stesso cartellino piccolo, indipendente dal formato scelto sopra
      document.body.setAttribute("data-print", target);
      setTimeout(() => { window.print(); document.body.removeAttribute("data-print"); clearPrintPageSize(); }, 50);
      return;
    }
    if (target === "internal") {
      openServerPdf(`/app/productions/${prodId}/internal-label.pdf`);
      return;
    }
    api.post(`/app/productions/${prodId}/print`).catch(() => {});
    // Finestra aperta subito, in modo sincrono, per evitare il blocco popup di
    // Safari quando l'URL reale arriva solo dopo la richiesta asincrona sotto.
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post(`/app/productions/${prodId}/label.pdf`,
        { format: fmt, label_type: labelType, label_data: labelData, qr_url: qrUrl },
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url; else window.open(url, "_blank");
    } catch {
      if (win) win.close();
      toast.error("Impossibile generare il PDF dell'etichetta.");
    }
  };
  // Etichetta consumatore: il server ripete la validazione e registra la
  // stampa (contenuto esatto, conferme) PRIMA che venga stampata; in presenza
  // di un punto rosso risponde 409 e non si stampa nulla.
  const printConsumer = async (asPdf = false) => {
    if (!consumerValidation || consumerValidation.status === "red") {
      toast.error("ETICHETTA NON PRONTA PER LA STAMPA: correggi i punti in rosso della checklist.");
      return;
    }
    // La dimensione dell'etichetta la decide il server: se il testo non
    // entra nel formato, l'etichetta viene allungata mantenendo la larghezza
    // del nastro (voci una sotto l'altra), mai tagliata.
    const win = asPdf ? preparePdfWindow() : isIOS() ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post(`/app/labels/productions/${prodId}/print`, { format: fmt, label_data: labelData, confirmed_warnings: confirmedWarnings });
      // Si stampa esattamente il contenuto validato e registrato dal server.
      flushSync(() => setConsumerValidation(data.validation));
      setPrintLogKey((k) => k + 1);
      if (asPdf) {
        await openServerPdfFile(`/app/labels/print-log/${data.log_id}/label.pdf`, { fileName: pdfFileName("etichetta", prod.recipe_name, prod.lot_code), win });
        return;
      }
      if (isIOS()) {
        await openServerPdf(`/app/labels/print-log/${data.log_id}/label.pdf`, undefined, win);
        return;
      }
      const layout = data.validation.layout;
      const measuredMm = labelRef.current ? Math.ceil((labelRef.current.scrollHeight * 25.4) / 96) : 0;
      setPrintPageSize(layout.width_mm, layout.mode === "long" ? Math.max(layout.height_mm, measuredMm) : layout.height_mm);
      document.body.setAttribute("data-print", "public");
      setTimeout(() => { window.print(); document.body.removeAttribute("data-print"); clearPrintPageSize(); }, 50);
    } catch (e) {
      if (win) win.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setValidationKey((k) => k + 1);
    }
  };
  const format = FORMATS.find((f) => f.key === fmt);
  const missingFields = useMemo(() => missingLabelFields(labelType, labelData, format, prod), [labelType, labelData, format, prod]);
  const b2bLayout = b2bMode && !format.small;
  // Lunghezza reale dell'etichetta B2B, ricalcolata a ogni modifica del testo.
  useLayoutEffect(() => {
    const el = labelRef.current;
    const next = b2bLayout && el?.dataset.testid === "b2b-label-preview" ? b2bFit(el, format) : null;
    setB2bSize((current) => (current?.scale === next?.scale && current?.lengthMm === next?.lengthMm ? current : next));
  }, [b2bLayout, format, prod, labelData, sleAssessment]);
  const pendingWarnings = (consumerValidation?.warnings || []).filter((w) => !confirmedWarnings.includes(w.code));
  const labelReady = consumerMode
    ? Boolean(consumerValidation && consumerValidation.status !== "red" && (!pendingWarnings.length))
    : missingFields.length === 0;
  const printLabel = consumerMode
    ? (!consumerValidation ? "Controllo in corso…" : consumerValidation.status === "red" ? "Etichetta non pronta per la stampa" : pendingWarnings.length ? "Conferma le verifiche per stampare" : "Stampa etichetta")
    : (labelReady ? "Stampa etichetta" : "Completa prima di stampare");
  const nutritionExemptConfigured = Boolean(consumerValidation?.checks.find((c) => c.key === "nutrizionale")?.notes?.some((n) => n.startsWith("Esenzione")));
  const internalUrl = prod ? `${window.location.origin}/vetrina/${prod.fixed_qr_token}` : "";
  const qrUrl = prod ? (labelType === "internal" ? internalUrl : `${window.location.origin}/t/${prod.public_token}`) : "";

  if (!prods) return <Spinner />;

  return (
    <div className="space-y-6" data-testid="etichette-page">
      {prod && <div className="no-print"><BlastTrace records={prod.blast_chilling_records} internal /></div>}
      {prod && sleAssessment && !consumerMode && (
        <Card className="p-4 no-print border-navy/20" data-testid="etichetta-shelf-life-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-navy text-gold-bright"><Hourglass className="h-4.5 w-4.5" /></div>
              <div>
                <div className="text-sm font-semibold text-navy">Shelf life calcolata dal DF Food Safety Expert Engine: {sleAssessment.result.shelf_life_days} giorni</div>
                <div className="text-xs text-slate-500">Calcolata sulla temperatura reale misurata nell'abbattimento ({sleAssessment.input.temp_c}°C) — sostituisce il valore manuale della ricetta sull'etichetta.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ShelfLifeResultBadge needsValidation={sleAssessment.result.needs_validation} />
              <Button variant="outline" onClick={() => setShowWhy(true)}><HelpCircle className="w-4 h-4" /> Perché?</Button>
            </div>
          </div>
        </Card>
      )}
      <ShelfLifeWhyDrawer assessment={showWhy ? sleAssessment : null} onClose={() => setShowWhy(false)} />
      <div className="flex items-end justify-between flex-wrap gap-4 no-print">
        <div><p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-body">Produzione</p><div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold">Etichette & QR</h1><AcademyTitleBadge /></div></div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/app/reports/traceability?production_id=${prodId}&origin=${encodeURIComponent(window.location.origin)}`, "_blank")} disabled={!prod} data-testid="trace-report"><FileText className="w-4 h-4" /> Report tracciabilità PDF</Button>
          <Button variant={labelReady ? "success" : "danger"} onClick={() => print("public")} disabled={!prod || (consumerMode && !labelReady)} data-testid="print-label"><Printer className="w-4 h-4" /> {printLabel}</Button>
          {labelReady && <Button variant="outline" onClick={() => print("public", true)} disabled={!prod} data-testid="pdf-label"><FileDown className="w-4 h-4" /> Crea PDF e stampa</Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 no-print">
        <Field label="Produzione / lotto"><Select value={prodId} onChange={(e) => setProdId(e.target.value)} data-testid="etichetta-prod">
          <option value="">Seleziona una produzione</option>
          {prods.map((p) => <option key={p.id} value={p.id}>{p.lot_code} — {p.recipe_name?.toUpperCase()} {p.blast_pending_count > 0 ? "— In attesa di conferma abbattimento" : p.blast_count > 0 ? "— Abbattuta" : "— Non abbattuta"}</option>)}
        </Select></Field>
        <Field label="Formato Brother QL-700"><Select value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="etichetta-format">{FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}</Select></Field>
      </div>

      {!prod && <Card className="p-10 text-center text-slate-400 no-print">Seleziona una produzione qui sopra per preparare la sua etichetta.</Card>}

      {prod && <div className="no-print">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Tipologia etichetta</p>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {LABEL_TYPES.map(({ key, label, icon: Icon, description }) => (
            <button key={key} type="button" onClick={() => chooseLabelType(key)} className={`rounded-xl border p-4 text-left transition-all ${labelCardKey(labelType, labelData) === key ?"border-gold bg-gold/10 ring-2 ring-gold/20" : "border-hairline bg-white hover:border-gold/60"}`} data-testid={`label-type-${key}`}>
              <span className="flex items-center gap-2 font-heading font-semibold text-navy"><Icon className="h-5 w-5 text-gold" />{label}</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-500">{description}</span>
            </button>
          ))}
        </div>
      </div>}

      {prod && mocaOn && <MocaLabelBox prod={prod} b2b={labelType === "commercial" && labelData.destination !== "consumer"} onChange={(lots) => setProd((current) => ({ ...current, moca_lots: lots }))} />}

      {/* «Senza glutine»: disponibile su ogni tipo di etichetta, la decide l'operatore. */}
      {prod && <GlutenFreeCard prod={prod} labelData={labelData} setLabelData={setLabelData} />}

      {prod && b2bMode && (
        <B2BQuickCard prod={prod} labelData={labelData} setLabelData={setLabelData}
          quantity={<NetQuantitySection data={labelData} onChange={changePack} defaults={packDefaults}
            canSaveDefault={PACK_EDITORS.includes(ctx?.user?.role)} onSaveDefault={savePackDefaults} saving={savingPackDefaults} />}
          shelfLifeDays={sleAssessment?.result?.shelf_life_days ?? prod.shelf_life_days}
          detailsOpen={b2bDetails} onToggleDetails={() => setB2bDetails((open) => !open)} />
      )}

      {prod && labelType !== "internal" && (!b2bMode || b2bDetails) && (
        <Card className="p-5 no-print" data-testid="label-extra-fields">
          <div className="mb-4 flex items-start gap-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>I dati vengono memorizzati per questa produzione. Compila quelli pertinenti prima della stampa; il formato 50×100 mm è consigliato per l’etichetta completa.</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {consumerMode && <div className="md:col-span-2 rounded-lg border border-hairline bg-slate-50 p-3 text-sm text-slate-600" data-testid="consumer-mode-info">Etichetta per il consumatore finale: denominazione, elenco ingredienti con subingredienti e allergeni, QUID, conservazione, data e operatore responsabile sono generati dalla <strong>scheda etichetta del prodotto</strong>, dalle <strong>schede tecniche delle materie prime</strong> e dall’<strong>anagrafica aziendale</strong>, e verificati prima della stampa.</div>}
            {!consumerMode && <>
            <Field label="Operatore alimentare / ragione sociale"><Input value={labelData.operatorName} onChange={(e) => setLabelData({ ...labelData, operatorName: e.target.value.toUpperCase() })} placeholder="ES. AZIENDA ROSSI S.R.L." /></Field>
            <Field label="Sede operativa"><Input value={labelData.operatorAddress} onChange={(e) => setLabelData({ ...labelData, operatorAddress: e.target.value.toUpperCase() })} placeholder="VIA, CIVICO, CAP, CITTÀ E PROVINCIA" /></Field>
            <Field label="Elenco ingredienti in ordine decrescente" hint="Gli allergeni tra parentesi e in maiuscolo saranno evidenziati in etichetta. Verifica ingredienti composti, additivi e percentuali."><textarea className={`${inputCls} min-h-[96px]`} value={labelData.ingredientsText} onChange={(e) => setLabelData({ ...labelData, ingredientsText: e.target.value })} placeholder="Es. Farina di frumento (GLUTINE), acqua, pomodoro..." /></Field>
            </>}
            {consumerMode && <div className="md:col-span-2"><NetQuantitySection data={labelData} onChange={changePack} consumer defaults={packDefaults}
              canSaveDefault={PACK_EDITORS.includes(ctx?.user?.role)} onSaveDefault={savePackDefaults} saving={savingPackDefaults} /></div>}
            {!consumerMode && <>
            <Field label="Indicazione della data"><Select value={labelData.expiryType} onChange={(e) => setLabelData({ ...labelData, expiryType: e.target.value })}><option value="use_by">Da consumare entro</option><option value="best_before">Da consumarsi preferibilmente entro</option></Select></Field>
            <Field label="Modalità di conservazione"><SuggestInput id="conservation" value={labelData.conservationOverride} onChange={(value) => setLabelData({ ...labelData, conservationOverride: value })} suggestions={SUGGESTIONS.conservation} placeholder="Scegli una modalità oppure scrivila" /></Field>
            </>}
            <Field label="Istruzioni d'uso / consumo"><SuggestInput id="consumption" value={labelData.instructions} onChange={(value) => setLabelData({ ...labelData, instructions: value })} suggestions={SUGGESTIONS.consumption} placeholder="Scegli un’istruzione oppure scrivila" /></Field>
            {labelType === "commercial" && <Field label="Origine o provenienza (se prevista)" hint="Seleziona soltanto un’indicazione verificata per il prodotto."><SuggestInput id="origin" value={labelData.origin} onChange={(value) => setLabelData({ ...labelData, origin: value })} suggestions={SUGGESTIONS.origin} placeholder="Scegli oppure inserisci l’origine" /></Field>}
            {labelType === "commercial" && !consumerMode && <Field label="Ingrediente caratterizzante / QUID"><SuggestInput id="quid" value={labelData.quid} onChange={(value) => setLabelData({ ...labelData, quid: value })} suggestions={SUGGESTIONS.quid} placeholder="Scegli un esempio e modifica ingrediente e percentuale" /></Field>}
          </div>
          {labelType === "commercial" && labelData.destination === "consumer" && (consumerMode ? !nutritionExemptConfigured : !labelData.nutritionExempt) && (
            <div className="mt-5 rounded-xl border border-hairline bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-heading font-semibold text-navy">Dichiarazione nutrizionale — valori medi per 100 g</h3>
                <div className="flex flex-wrap items-center gap-2">
                  {nutritionAssessment && <NutritionResultBadge needsValidation={nutritionAssessment.result.needs_validation} />}
                  <Button variant="outline" loading={computingNutrition} onClick={computeNutrition} data-testid="nutrition-compute"><Calculator className="w-4 h-4" /> Calcola dalla ricetta</Button>
                  {nutritionAssessment && <Button variant="outline" onClick={() => setShowNutritionWhy(true)} data-testid="nutrition-why"><HelpCircle className="w-4 h-4" /> Perché?</Button>}
                  {nutritionAssessment && <Button variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/app/nutrition/assessments/${nutritionAssessment.id}/pdf`, "_blank")} data-testid="nutrition-pdf"><Download className="w-4 h-4" /> Scarica PDF</Button>}
                </div>
              </div>
              <p className="mt-1 text-xs text-slate-500">"Calcola dalla ricetta" usa gli ingredienti collegati in Materie prime; puoi comunque correggere manualmente i valori qui sotto prima di stampare.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {NUTRIENTS.map(([key, label]) => <Field key={key} label={label}><Input inputMode="decimal" value={labelData[key]} onChange={(e) => setLabelData({ ...labelData, [key]: e.target.value })} placeholder="0" /></Field>)}
              </div>
            </div>
          )}
          <NutritionWhyDrawer assessment={showNutritionWhy ? nutritionAssessment : null} onClose={() => setShowNutritionWhy(false)} />
          {!b2bMode && <div className="mt-4 flex flex-wrap gap-5 text-sm text-navy">
            {labelType === "commercial" && labelData.destination === "consumer" && !consumerMode && <label className="flex items-center gap-2"><input type="checkbox" checked={labelData.nutritionExempt} onChange={(e) => setLabelData({ ...labelData, nutritionExempt: e.target.checked })} /> Esente dalla dichiarazione nutrizionale</label>}
            <label className="flex items-center gap-2"><input type="checkbox" checked={labelData.defrosted} onChange={(e) => setLabelData({ ...labelData, defrosted: e.target.checked })} /> Prodotto decongelato</label>
            {prod.trace_labels?.length > 0 && !consumerMode && <label className="flex items-center gap-2"><input type="checkbox" checked={labelData.traceConfirmed} onChange={(e) => setLabelData({ ...labelData, traceConfirmed: e.target.checked })} /> Confermo che le tracce derivano dalla valutazione del rischio</label>}
          </div>}
          {labelType === "commercial" && labelData.destination === "consumer" && !consumerMode && labelData.nutritionExempt && <div className="mt-4"><Field label="Motivazione documentata dell’esenzione"><SuggestInput id="exemption" value={labelData.exemptionReason} onChange={(value) => setLabelData({ ...labelData, exemptionReason: value })} suggestions={SUGGESTIONS.exemption} placeholder="Scegli una motivazione soltanto se realmente applicabile" /></Field></div>}
        </Card>
      )}

      {prod && consumerMode && (
        <LabelProfileEditor recipeId={prod.recipe_id} recipeName={prod.recipe_name} process={consumerValidation?.process}
          quidTerms={consumerValidation?.quid_terms || []} quidIssues={consumerValidation?.checks.find((c) => c.key === "quid")?.issues || []}
          meta={labelMeta} canEdit={canEditProfile} onSaved={() => setValidationKey((k) => k + 1)} />
      )}

      {prod && consumerMode && (
        <ConsumerLabelChecklist validation={consumerValidation} loading={validating} confirmed={confirmedWarnings}
          onToggleConfirm={(code) => setConfirmedWarnings((current) => current.includes(code) ? current.filter((c) => c !== code) : [...current, code])} />
      )}

      {prod && !consumerMode && (
        <Card className={`p-4 no-print ${labelReady ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="label-validation">
          <div className="flex items-start gap-3">
            {labelReady ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />}
            <div><div className={`font-semibold ${labelReady ? "text-emerald-800" : "text-red-800"}`}>{labelReady ? "Controlli di compilazione superati" : "Etichetta non pronta per la stampa"}</div>
            <div className="mt-1 text-sm text-slate-600">{labelReady ? "I campi configurabili richiesti per questa tipologia risultano compilati. Resta necessaria la verifica professionale delle informazioni inserite." : missingFields.join(" · ")}</div></div>
          </div>
        </Card>
      )}

      {prod && (
        <div className="flex flex-wrap gap-6 items-start">
          <div className="max-w-full overflow-x-auto">
            {consumerMode
              ? <ConsumerLabelPreview labelRef={labelRef} content={consumerValidation?.content} layout={consumerValidation?.layout} />
              : b2bLayout
                ? <B2BLabel labelRef={labelRef} prod={prod} format={format} qrUrl={qrUrl} labelData={labelData} scale={b2bSize?.scale} shelfLifeDays={sleAssessment?.result?.shelf_life_days ?? prod.shelf_life_days} />
                : <LabelPreview labelRef={labelRef} prod={prod} format={format} qrUrl={qrUrl} labelType={labelType} labelData={labelData} shelfLifeDays={sleAssessment?.result?.shelf_life_days ?? prod.shelf_life_days} />}
          </div>
          <Card className="p-5 max-w-sm no-print">
            <h3 className="font-heading font-semibold mb-2">Anteprima in scala</h3>
            {b2bLayout
              ? <p className="text-sm text-slate-500 font-body" data-testid="b2b-size-note">Etichetta B2B verticale, larga quanto il nastro: <strong>{format.heightMm} × {b2bSize?.lengthMm || format.widthMm} mm</strong>. {b2bSize?.lengthMm > format.widthMm ? "Testo molto lungo: anche al carattere minimo non entra in 100 mm, l'etichetta si allunga (imposta la stessa lunghezza nella stampante)." : b2bSize?.scale < 1 ? "Caratteri ridotti in modo uniforme per far stare tutto nel formato, a tutta larghezza." : "Tutto il contenuto entra nel formato standard."}</p>
              : <p className="text-sm text-slate-500 font-body">L'etichetta è mostrata nelle proporzioni reali del formato <strong>{format.label}</strong>. La stampa genera un PDF nel formato esatto.</p>}
            {consumerMode && consumerValidation?.layout?.mode === "long" && <p className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-2 text-sm text-blue-900" data-testid="long-label-note">Il testo non entra nel formato standard: l'etichetta viene allungata a <strong>{consumerValidation.layout.width_mm} × {consumerValidation.layout.height_mm} mm</strong> mantenendo la larghezza del nastro ({consumerValidation.layout.width_mm} mm), con le voci una sotto l'altra.</p>}
            <div className="mt-3 text-xs text-slate-400">{labelType === "internal" ? "QR fisso e pubblico: valido anche in vetrina, si stampa una sola volta e mostra sempre l'ultima produzione." : "QR pubblico: univoco per questo lotto, contiene denominazione, allergeni, lotto, date, conservazione e rintracciabilità del prodotto."}</div>
            {prod.print_history?.length > 0 && <div className="mt-3 text-xs text-slate-400">Stampe registrate: {prod.print_history.length}</div>}
          </Card>
        </div>
      )}
      {/* Copia dedicata al flusso window.print() (Android/desktop), portalata in document.body:
          <main class="app-content"> ha overflow-x-clip, che lo rende containing block dei
          discendenti position:absolute (spec CSS Overflow), spostando l'origine della pagina di
          stampa lontano dal vero angolo pagina. Su iOS questa copia resta inutilizzata (si stampa
          il PDF generato dal backend). */}
      {prod && createPortal(
        <div className="print-area print-public" data-format={format.key}>
          {consumerMode
            ? <ConsumerLabelPreview content={consumerValidation?.content} layout={consumerValidation?.layout} />
            : b2bLayout
              ? <B2BLabel prod={prod} format={format} qrUrl={qrUrl} labelData={labelData} scale={b2bSize?.scale} lengthMm={b2bSize?.lengthMm} shelfLifeDays={sleAssessment?.result?.shelf_life_days ?? prod.shelf_life_days} />
              : <LabelPreview prod={prod} format={format} qrUrl={qrUrl} labelType={labelType} labelData={labelData} shelfLifeDays={sleAssessment?.result?.shelf_life_days ?? prod.shelf_life_days} />}
        </div>,
        document.body
      )}

      {prod && (
        <div className="flex flex-wrap gap-6 items-start" data-testid="internal-qr-section">
          <div className="max-w-full overflow-x-auto">
            <InternalQrCard prod={prod} internalUrl={internalUrl} />
          </div>
          <Card className="p-5 max-w-sm no-print">
            <h3 className="font-heading font-semibold mb-2 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-navy" /> QR fisso di tracciabilità / vetrina</h3>
            <p className="text-sm text-slate-500 font-body">Pubblico, nessun login richiesto: chiunque lo scansioni (anche il cliente in vetrina) vede denominazione, allergeni, lotto, date, conservazione e gli eventuali abbattimenti dell'ultima produzione. Fornitori, quantità e operatore restano visibili solo al personale dentro l'app.</p>
            <p className="mt-2 text-xs text-slate-400 font-body"><strong>A differenza del QR pubblico/di vendita</strong> (diverso per ogni produzione, perché è un documento di tracciabilità legale legato al singolo lotto), questo QR è <strong>fisso per questa ricetta</strong>: stampalo una sola volta — anche per la vetrina — e resta valido per sempre, aggiornandosi da solo all'ultima produzione ogni volta che viene scansionato. Stesso principio dei QR del libro ingredienti.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => print("internal")} data-testid="print-internal-qr"><Printer className="w-4 h-4" /> Stampa QR fisso</Button>
              <Button variant="outline" onClick={() => print("internal", true)} data-testid="pdf-internal-qr"><FileDown className="w-4 h-4" /> Crea PDF e stampa</Button>
            </div>
          </Card>
        </div>
      )}
      {prod && createPortal(
        <div className="print-area print-internal">
          <InternalQrCard prod={prod} internalUrl={internalUrl} />
        </div>,
        document.body
      )}

      {prod && consumerMode && <LabelPrintLog productionId={prod.id} refreshKey={printLogKey} />}

      {prod?.used_lots?.length > 0 && (
        <Card className="p-5 no-print" data-testid="etichetta-ingredienti-lotti">
          <h3 className="font-heading font-semibold mb-3">Ingredienti e lotti utilizzati in questa produzione</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-hairline">
                  <th className="py-2 pr-4">Ingrediente</th>
                  <th className="py-2 pr-4">Lotto</th>
                  <th className="py-2 pr-4">Fornitore</th>
                  <th className="py-2">Scadenza</th>
                </tr>
              </thead>
              <tbody>
                {flattenUsedLots(prod.used_lots).map(({ lot: u, depth }, i) => (
                  <tr key={i} className={`border-b border-hairline last:border-0 ${depth ? "bg-slate-50" : ""}`} data-testid={`used-lot-row-${i}`}>
                    <td className="py-2 pr-4 text-navy font-medium" style={depth ? { paddingLeft: `${depth * 1.25}rem` } : undefined}>
                      {depth > 0 && <span className="mr-1 text-slate-400" aria-hidden="true">↳</span>}{u.ingredient_name}
                      {u.internal && <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">Semilavorato</span>}
                    </td>
                    <td className="py-2 pr-4 font-mono text-gold"><LotTraceLink lotId={u.lot_id} auto={u.auto || u.internal}>{u.supplier_lot || "—"}</LotTraceLink>{u.auto && <span className="ml-1 text-[10px] text-slate-400">(auto)</span>}</td>
                    <td className="py-2 pr-4 text-navy">{u.internal && u.produced_on ? `${u.supplier_name} del ${formatItalianDate(u.produced_on)}` : u.supplier_name || "—"}</td>
                    <td className="py-2 text-navy">{formatItalianDate(u.expiry_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// Modulo MOCA: imballaggio associato alla produzione. La domanda compare solo
// se manca e la ricetta non l'ha esclusa; la sezione resta sempre disponibile.
function MocaLabelBox({ prod, b2b, onChange }) {
  const [askDisabled, setAskDisabled] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setDismissed(false);
    // Ricetta configurata in MOCA › Ricette (MOCA associati oppure «Senza MOCA»): nessuna domanda.
    api.get(`/app/moca/productions/${prod.id}`).then(({ data }) => setAskDisabled(data.ask_disabled || data.default_item_ids.length > 0)).catch(() => setAskDisabled(true));
  }, [prod.id]);
  const lots = prod.moca_lots || [];
  if (!lots.length && !askDisabled && !dismissed) {
    return <div className="no-print"><MocaPrompt productions={[{ id: prod.id, recipe_name: prod.recipe_name, lot_code: prod.lot_code }]} onDone={() => setDismissed(true)} onAssociated={(_, saved) => onChange(saved)} /></div>;
  }
  return <Card className={`no-print flex flex-wrap items-center justify-between gap-3 p-4 ${b2b && !lots.length ? "border-amber-300 bg-amber-50" : ""}`} data-testid="label-moca">
    <div className="min-w-0 text-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Imballaggi MOCA</div>
      {lots.length ? lots.map((m) => <div key={m.lot_id} className="font-medium text-navy">{mocaLabelText(m)}</div>)
        : <div className="text-slate-500">{b2b ? "Etichetta B2B senza MOCA: tipologia e lotto dell'imballaggio non compariranno." : "Nessun MOCA associato a questa produzione."}</div>}
    </div>
    <Button variant="outline" onClick={() => setEditing(true)} data-testid="label-moca-edit">{lots.length ? "Modifica MOCA" : "Associa MOCA"}</Button>
    {editing && <MocaProductionDialog productionId={prod.id} onClose={() => setEditing(false)} onSaved={(saved) => { setEditing(false); onChange(saved); }} />}
  </Card>;
}

// Etichetta B2B rapida: operatore, ingredienti, conservazione e date arrivano
// da anagrafica, ricetta e produzione; si sceglie solo la quantità. I campi
// completi restano dietro "Modifica dettagli".
// Dicitura volontaria "senza glutine" (Reg. UE 828/2014): la decide
// l'operatore; se la produzione dichiara glutine (allergeni o tracce) si
// avvisa, senza bloccare.
const declaresGluten = (prod) => [...(prod.allergens || []), ...(prod.traces || [])].includes("glutine");

function GlutenFreeCard({ prod, labelData, setLabelData }) {
  return (
    <Card className="p-4 no-print" data-testid="gluten-free-card">
      <label className="flex items-center gap-2 text-sm font-semibold text-navy">
        <input type="checkbox" className="h-4 w-4" checked={Boolean(labelData.glutenFree)} onChange={(e) => setLabelData((current) => ({ ...current, glutenFree: e.target.checked }))} data-testid="label-gluten-free" />
        Stampa la dicitura «Senza glutine» su questa etichetta
      </label>
      {labelData.glutenFree && <GlutenFreeNote prod={prod} />}
    </Card>
  );
}

function GlutenFreeNote({ prod }) {
  if (declaresGluten(prod)) {
    return <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="gluten-free-conflict">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span><strong>Attenzione:</strong> questa produzione dichiara il glutine tra gli allergeni o le tracce. Stampando «Senza glutine» l'etichetta sarebbe in contraddizione: togli la spunta, oppure correggi allergeni e tracce della ricetta.</span>
    </div>;
  }
  return <p className="mt-3 rounded-lg border border-hairline bg-slate-50 p-3 text-xs text-slate-600" data-testid="gluten-free-note">
    «Senza glutine» si può usare solo se il prodotto finito contiene al massimo 20 mg/kg di glutine (Reg. UE n. 828/2014) e l'azienda può dimostrarlo (ingredienti idonei, procedure contro la contaminazione crociata, analisi).
  </p>;
}

function B2BQuickCard({ prod, labelData, setLabelData, quantity, shelfLifeDays, detailsOpen, onToggleDetails }) {
  const set = (patch) => setLabelData((current) => ({ ...current, ...patch }));
  const scad = shelfLifeDays != null ? new Date(new Date(prod.created_at).getTime() + shelfLifeDays * 86400000) : null;
  const rows = [
    ["Operatore", [labelData.operatorName, labelData.operatorAddress].filter((v) => v.trim()).join(" — "), labelData.operatorName.trim() && labelData.operatorAddress.trim()],
    ["Ingredienti", labelData.ingredientsText, labelData.ingredientsText.trim()],
    ["Conservazione", conservationDisplayText((labelData.conservationOverride ?? prod.conservation ?? "").trim()), (labelData.conservationOverride ?? prod.conservation ?? "").trim()],
    [labelData.expiryType === "best_before" ? "Da consumarsi pref. entro" : "Da consumare entro", scad ? formatItalianDate(scad) : "", true],
    ...(labelData.instructions ? [["Uso", labelData.instructions, true]] : []),
    ...(labelData.origin ? [["Origine", labelData.origin, true]] : []),
    ...(labelData.quid ? [["QUID", labelData.quid, true]] : []),
  ];
  return (
    <Card className="p-5 no-print" data-testid="b2b-quick">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-navy"><Zap className="h-4 w-4 text-gold" /> Etichetta B2B rapida</h3>
        <span className="text-xs text-slate-500">Scegli la quantità: il resto è già compilato.</span>
      </div>

      <div className="mt-4">{quantity}</div>

      <div className="mt-4 flex flex-wrap gap-5 text-sm text-navy">
        <label className="flex items-center gap-2"><input type="checkbox" checked={labelData.defrosted} onChange={(e) => set({ defrosted: e.target.checked })} /> Prodotto decongelato</label>
        {prod.trace_labels?.length > 0 && <label className="flex items-center gap-2"><input type="checkbox" checked={labelData.traceConfirmed} onChange={(e) => set({ traceConfirmed: e.target.checked })} /> Confermo le tracce ({prod.trace_labels.join(", ")}) dalla valutazione del rischio</label>}
      </div>

      <div className="mt-4 rounded-lg border border-hairline bg-slate-50 p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Compilato in automatico</span>
          <Button variant="outline" onClick={onToggleDetails} data-testid="b2b-details-toggle"><Pencil className="h-4 w-4" /> {detailsOpen ? "Nascondi dettagli" : "Modifica dettagli"}</Button>
        </div>
        <dl className="grid gap-1 text-sm">
          {rows.map(([label, value, ok]) => (
            <div key={label} className="grid grid-cols-[minmax(0,9rem)_1fr] gap-2">
              <dt className="text-slate-500">{label}</dt>
              <dd className={`min-w-0 truncate ${ok ? "text-navy" : "font-semibold text-red-600"}`} title={value}>{value || (ok ? "—" : "Da completare in «Modifica dettagli»")}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 text-[11px] text-slate-400">Quantità, origine, QUID e tipo di data vengono ricordati per le prossime produzioni di questa ricetta.</p>
      </div>
    </Card>
  );
}

// Etichetta B2B: copia HTML di _render_b2b (label_pdf.py), stesse misure.
// Tutto è espresso in --u (1 mm in stampa, 1,5 mm a schermo per leggerla
// meglio, vedi index.css); pt() converte i punti tipografici del PDF.
// Solo nero: le stampanti termiche non stampano colori.
// sk()/spt() sono ridotti dalla scala --k (caratteri e spazi), u()/pt() no
// (larghezza, margini e spessore delle linee restano fissi).
const u = (n) => `calc(var(--u) * ${n})`;
const pt = (n) => u(+(n * 0.3528).toFixed(4));
const sk = (n) => `calc(var(--u) * var(--k) * ${n})`;
const spt = (n) => sk(+(n * 0.3528).toFixed(4));
const B2B_KEY = { fontSize: spt(5.6), lineHeight: spt(6.6), fontWeight: 700, textTransform: "uppercase" };
// Margine interno: 1,8 mm dal taglio come B2B_PAD (0,4 bordo esterno + 0,3 linea + 1,1).
const B2B_INNER = 1.1;
const B2B_KEY_W = 22;
// Corpo del lotto in Courier (0,6 em a carattere), uguale a b2b_lot_size in
// label_pdf.py: il più grande che sta su una riga, tra 6,5 e 10,5 pt × --k.
// In CSS perché dipende da --k, che la misura fa variare senza ridisegnare.
const b2bLotFont = (lot, tapeMm) => {
  const perMm = 0.95 / (0.6 * Math.max(lot.length, 1) * 0.3528);
  const fit = `(${tapeMm - 2 * 1.8} - ${B2B_KEY_W + 2 * 1.3} * var(--k)) * ${perMm.toFixed(5)}`;
  return `calc(var(--u) * 0.3528 * clamp(6.5 * var(--k), ${fit}, 10.5 * var(--k)))`;
};

function B2BLabel({ labelRef, prod, format, qrUrl, labelData, shelfLifeDays, scale = 1, lengthMm }) {
  const conservation = conservationDisplayText((labelData.conservationOverride ?? prod.conservation ?? "").trim());
  const consumption = (labelData.instructions ?? "").trim();
  const dt = new Date(prod.created_at);
  const scad = shelfLifeDays != null ? new Date(dt.getTime() + shelfLifeDays * 86400000) : null;
  const composition = labelData.packMode && labelData.packMode !== "single" ? labelData.packComposition : "";
  const traces = labelData.traceConfirmed ? prod.trace_labels || [] : [];
  const allergens = prod.allergen_labels || [];
  const rows = [
    { key: "Lotto", value: prod.lot_code, lot: true },
    labelData.netQuantity && { key: composition ? "Quantità netta totale" : "Quantità netta", value: labelData.netQuantity, sub: composition, testId: "label-preview-quantity" },
    { key: "Prodotto il", value: formatItalianDate(dt) },
    { key: labelData.expiryType === "best_before" ? "Da consumarsi preferibilmente entro" : "Da consumare entro", value: scad ? formatItalianDate(scad) : "—", expiry: true },
  ].filter(Boolean);
  const details = [
    labelData.origin && ["Origine", labelData.origin],
    labelData.quid && ["Ingrediente caratterizzante", labelData.quid],
    ...(prod.moca_lots || []).map((m) => ["Imballaggio (MOCA)", mocaLabelText(m)]),
  ].filter(Boolean);
  const line = `${pt(0.5)} solid #000`;
  const lotFont = b2bLotFont(prod.lot_code || "", format.heightMm);
  const frame = `${pt(0.9)} solid #000`;
  const section = (title, body) => (
    <div style={{ marginTop: sk(2.2) }}>
      <div style={{ fontSize: spt(6), lineHeight: spt(7), fontWeight: 700, textTransform: "uppercase", marginBottom: sk(0.5) }}>{title}</div>
      <div style={{ fontSize: spt(8), lineHeight: spt(9.6) }}>{body}</div>
    </div>
  );
  return (
    <div ref={labelRef} className="b2b-label" data-testid="b2b-label-preview"
      style={{ width: u(format.heightMm), [lengthMm ? "height" : "minHeight"]: u(lengthMm || format.widthMm), padding: u(0.4), "--k": scale, boxSizing: "border-box", background: "#fff", color: "#000",
        fontFamily: "Helvetica, Arial, sans-serif", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact", breakInside: "avoid", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, boxSizing: "border-box", border: frame, borderRadius: u(1.4), overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ height: sk(6), background: "#000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${u(B2B_INNER)}`, fontSize: spt(6.5), fontWeight: 700, flexShrink: 0 }}>
          <span>FORNITURA B2B</span><span>USO PROFESSIONALE</span>
        </div>
        <div style={{ padding: `0 ${u(B2B_INNER)} ${u(B2B_INNER)}`, display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ marginTop: sk(2.2), display: "flex", gap: sk(2.5), alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: spt(15), lineHeight: spt(16.2), fontWeight: 700 }}>{frozenDenomination(prod.recipe_name?.toUpperCase(), prod.post_cooking_destination)}</div>
            <div style={{ width: sk(17), flexShrink: 0, textAlign: "center" }}>
              <QRCodeCanvas value={qrUrl} size={128} fgColor="#000" style={{ width: sk(17), height: sk(17), display: "block" }} />
              <div style={{ marginTop: sk(0.6), fontSize: spt(5), lineHeight: spt(5.8), fontWeight: 700 }}>TRACCIABILITÀ</div>
            </div>
          </div>
          <div style={{ marginTop: sk(2.2), border: frame }}>
            {rows.map((row, i) => (
              <div key={row.key} style={{ display: "grid", gridTemplateColumns: `${sk(B2B_KEY_W)} 1fr`, background: row.expiry ? "#000" : "#fff", color: row.expiry ? "#fff" : "#000", borderTop: i ? line : "none" }}>
                <div style={{ ...B2B_KEY, padding: sk(1.3), borderRight: line, display: "flex", alignItems: "center" }}>{row.key}</div>
                <div style={{ padding: sk(1.3), display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <div data-testid={row.testId} style={row.lot
                    ? { fontSize: lotFont, lineHeight: 1.15, fontWeight: 700, fontFamily: "'Courier New', Courier, monospace", whiteSpace: "nowrap" }
                    : { fontSize: spt(row.expiry ? 13 : 10.5), lineHeight: spt(row.expiry ? 14.6 : 12), fontWeight: 700, overflowWrap: "anywhere" }}>{row.value}</div>
                  {row.sub && <div style={{ marginTop: sk(0.3), fontSize: spt(7), lineHeight: spt(8.2) }}>{row.sub}</div>}
                </div>
              </div>
            ))}
          </div>
          {labelData.glutenFree && (
            <div style={{ marginTop: sk(2.2), height: sk(6), background: "#000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: spt(9.5), fontWeight: 700 }} data-testid="b2b-label-gluten-free">SENZA GLUTINE</div>
          )}
          {(allergens.length > 0 || traces.length > 0) && (
            <div style={{ marginTop: sk(2.2), border: frame }}>
              <div style={{ height: sk(4.8), background: "#000", color: "#fff", display: "flex", alignItems: "center", padding: `0 ${sk(1.3)}`, fontSize: spt(6.5), fontWeight: 700 }}>{allergens.length ? "CONTIENE ALLERGENI" : "TRACCE"}</div>
              <div style={{ padding: sk(1.3) }}>
                {allergens.length > 0 && <div style={{ fontSize: spt(9.5), lineHeight: spt(11.2), fontWeight: 700 }}>{allergens.map((a) => a.toUpperCase()).join(" · ")}</div>}
                {traces.length > 0 && <div style={{ marginTop: allergens.length ? sk(0.6) : 0, fontSize: spt(7), lineHeight: spt(8.4) }}><strong>Può contenere:</strong> {traces.join(", ")}</div>}
              </div>
            </div>
          )}
          {labelData.ingredientsText && section("Ingredienti", <IngredientText text={labelData.ingredientsText} />)}
          {conservation && section("Conservazione", conservation)}
          {consumption && section("Modalità d'uso", consumption)}
          {details.length > 0 && (
            <div style={{ marginTop: sk(2.2), display: "grid", gridTemplateColumns: `${sk(B2B_KEY_W)} 1fr`, rowGap: sk(1) }}>
              {details.map(([key, value], i) => (
                <React.Fragment key={i}>
                  <div style={{ ...B2B_KEY, paddingRight: sk(1) }}>{key}</div>
                  <div style={{ fontSize: spt(7), lineHeight: spt(8.4) }}>{value}</div>
                </React.Fragment>
              ))}
            </div>
          )}
          {labelData.defrosted && <div style={{ marginTop: sk(2.2), background: "#000", color: "#fff", padding: `${sk(0.8)} ${sk(1.3)}`, fontSize: spt(6.5), fontWeight: 700 }}>PRODOTTO DECONGELATO</div>}
          {(labelData.operatorName || labelData.operatorAddress) && (
            <div style={{ marginTop: "auto", paddingTop: sk(2.2) }}>
              <div style={{ borderTop: line, paddingTop: sk(1.6) }}>
                <div style={{ fontSize: spt(6), lineHeight: spt(7), fontWeight: 700, textTransform: "uppercase", marginBottom: sk(0.5) }}>Operatore responsabile</div>
                {labelData.operatorName && <div style={{ fontSize: spt(8), lineHeight: spt(9.4), fontWeight: 700 }}>{labelData.operatorName}</div>}
                {labelData.operatorAddress && <div style={{ fontSize: spt(7), lineHeight: spt(8.4) }}>{labelData.operatorAddress}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LabelPreview({ labelRef, prod, format, qrUrl, labelType, labelData, shelfLifeDays }) {
  const conservation = conservationDisplayText((labelData.conservationOverride ?? prod.conservation ?? "").trim());
  const consumption = (labelData.instructions ?? "").trim();
  const dt = new Date(prod.created_at);
  const scad = shelfLifeDays != null ? new Date(dt.getTime() + shelfLifeDays * 86400000) : null;
  const textSize = labelType === "internal" ? (format.small ? 8 : 10) : 10;
  return (
    <div ref={labelRef} className={`bg-white border-2 border-navy rounded-md shadow-card ${format.small ? "p-2" : "p-2.5"}`} style={{ width: format.w, minHeight: format.h }} data-testid="label-preview">
      <div className="mb-1 text-[9px] font-bold uppercase tracking-widest text-gold">{LABEL_HEADER[labelType]}{labelType === "commercial" ? (labelData.destination === "consumer" ? " · CONSUMATORE" : " · B2B") : ""}</div>
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <div className="font-heading font-bold text-navy leading-tight" style={{ fontSize: format.small ? 13 : 16 }}>{frozenDenomination(prod.recipe_name?.toUpperCase(), prod.post_cooking_destination)}</div>
          <div className="font-mono text-gold" style={{ fontSize: format.small ? 9 : 12 }}>{format.small ? "" : "Lotto: "}{prod.lot_code}</div>
        </div>
        <QRCodeCanvas value={qrUrl} size={format.small ? 40 : 68} fgColor="#0A1F44" />
      </div>
      {labelType !== "internal" && labelData.ingredientsText && (
        <div className="mt-1.5 text-navy leading-snug" style={{ fontSize: textSize }}>
          <strong>Ingredienti: </strong><IngredientText text={labelData.ingredientsText} />
        </div>
      )}
      {prod.allergen_labels?.length > 0 && (
        <div className="mt-1 flex items-start gap-1" style={{ fontSize: textSize }}>
          <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" />
          <span className="text-red-700 font-bold uppercase">Allergeni: {prod.allergen_labels.join(", ")}</span>
        </div>
      )}
      {prod.trace_labels?.length > 0 && (labelType === "internal" || labelData.traceConfirmed) && (
        <div className="mt-0.5 text-amber-800" style={{ fontSize: textSize }}>Può contenere: {prod.trace_labels.join(", ")}</div>
      )}
      {labelData.glutenFree && <div className="mt-0.5 font-bold text-navy" style={{ fontSize: textSize }} data-testid="label-preview-gluten-free">SENZA GLUTINE</div>}
      <div className="mt-1.5 grid grid-cols-2 gap-x-2 text-navy leading-snug" style={{ fontSize: textSize }}>
        {format.small ? (
          <div className="col-span-2">Prod.: {formatItalianDate(dt)}{scad && ` · Scad.: ${formatItalianDate(scad)}`}</div>
        ) : (<>
          <div>Prod.: {formatItalianDate(dt)}</div>
          {scad && <div>{labelData.expiryType === "best_before" ? "Da consumarsi preferibilmente entro" : "Da consumare entro"}: {formatItalianDate(scad)}</div>}
        </>)}
        {labelType === "commercial" && labelData.netQuantity && <div className="col-span-2 font-semibold" data-testid="label-preview-quantity">{netQuantityLine(labelData.netQuantity, labelData.packMode && labelData.packMode !== "single" ? labelData.packComposition : "")}</div>}
        {conservation && <div className="col-span-2">Conserv.: {conservation}</div>}
        {!format.small && consumption && <div className="col-span-2">Uso: {consumption}</div>}
        {labelData.defrosted && <div className="col-span-2 font-semibold">PRODOTTO DECONGELATO</div>}
        {labelType === "commercial" && labelData.origin && <div className="col-span-2">Origine: {labelData.origin}</div>}
        {labelType === "commercial" && labelData.quid && <div className="col-span-2">Ingrediente caratterizzante: {labelData.quid}</div>}
        {labelType === "commercial" && labelData.destination !== "consumer" && (prod.moca_lots || []).map((m) => <div key={m.lot_id} className="col-span-2">MOCA: {mocaLabelText(m)}</div>)}
      </div>
      {!format.small && labelType === "internal" && prod.used_lots?.length > 0 && (
        <div className="mt-1 pt-1 border-t border-navy/20 text-navy" style={{ fontSize: 8 }}>
          <div className="font-semibold uppercase tracking-wide" style={{ fontSize: 8 }}>Ingredienti e lotti</div>
          {prod.used_lots.map((u, i) => (
            <div key={i} className="flex justify-between gap-2 leading-tight">
              <span className="truncate">{u.ingredient_name}</span>
              <span className="font-mono shrink-0">{u.supplier_lot || "—"}</span>
            </div>
          ))}
        </div>
      )}
      {!format.small && labelType === "commercial" && labelData.destination === "consumer" && !labelData.nutritionExempt && (
        <div className="mt-1 border-t border-navy/20 pt-1 text-navy leading-snug" style={{ fontSize: 9 }}>
          <strong>Valori medi per 100 g:</strong> energia {labelData.energyKj} kJ / {labelData.energyKcal} kcal; grassi {labelData.fat} g, di cui saturi {labelData.saturates} g; carboidrati {labelData.carbohydrate} g, di cui zuccheri {labelData.sugars} g; proteine {labelData.protein} g; sale {labelData.salt} g.
        </div>
      )}
      {!format.small && labelType !== "internal" && (labelData.operatorName || labelData.operatorAddress) && <div className="mt-1 border-t border-navy/20 pt-1 text-slate-700 leading-snug" style={{ fontSize: 9 }}>Operatore responsabile: {labelData.operatorName}{labelData.operatorAddress ? ` — ${labelData.operatorAddress}` : ""}</div>}
    </div>
  );
}

// QR fisso/vetrina: fisso per ricetta+sede (non per questa singola
// produzione, vedi nota in Etichette.jsx più sotto), per questo il
// cartellino non riporta il lotto (cambia a ogni produzione) ma solo il nome
// della ricetta. Pubblico: può stare anche in vetrina.
function InternalQrCard({ prod, internalUrl }) {
  return (
    <div className="bg-white border-2 border-navy border-dashed rounded-md p-3" style={{ width: 248 }} data-testid="internal-label">
      <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-navy bg-gold/20 px-1.5 py-0.5 rounded"><RefreshCw className="w-2.5 h-2.5" /> Tracciabilità</div>
      <div className="flex justify-between items-start gap-2 mt-1">
        <div className="min-w-0">
          <div className="font-heading font-bold text-navy leading-tight" style={{ fontSize: 13 }}>{frozenDenomination(prod.recipe_name?.toUpperCase(), prod.post_cooking_destination)}</div>
          <div className="text-gold" style={{ fontSize: 8 }}>Aggiornato automaticamente a ogni produzione</div>
        </div>
        <QRCodeCanvas value={internalUrl} size={54} fgColor="#0A1F44" />
      </div>
      <div className="no-print mt-1 text-navy" style={{ fontSize: 8 }}>Scansiona per l'ultima produzione: denominazione, allergeni, lotto e date</div>
    </div>
  );
}

function IngredientText({ text }) {
  return String(text).split(/(\([^)]+\))/g).map((part, index) =>
    /^\([^)]+\)$/.test(part) ? <strong key={index} className="uppercase">{part}</strong> : <React.Fragment key={index}>{part}</React.Fragment>
  );
}

function SuggestInput({ id, value, onChange, suggestions, placeholder, uppercase = true }) {
  const listId = `label-suggestions-${id}`;
  return (
    <>
      <Input list={listId} value={value} onChange={(event) => onChange(uppercase ? event.target.value.toUpperCase() : event.target.value)} placeholder={placeholder} autoComplete="off" />
      <datalist id={listId}>{suggestions.map((suggestion) => <option key={suggestion} value={suggestion} />)}</datalist>
      <span className="mt-1 block text-[11px] text-slate-400">Apri la tendina per scegliere rapidamente oppure digita un testo personalizzato.</span>
    </>
  );
}

// Semilavorati: sotto la riga della besciamella compaiono i lotti usati per
// produrla (a cascata), così la tracciabilità all'indietro è completa.
function flattenUsedLots(lots, depth = 0) {
  return (lots || []).flatMap((lot) => [{ lot, depth }, ...flattenUsedLots(lot.sub_lots, depth + 1)]);
}
